type OverflowAncestors = Array<Element | Window | VisualViewport>;

export type Alignment = 'start' | 'end';
export type Side = 'top' | 'right' | 'bottom' | 'left';
export type AlignedPlacement = `${Side}-${Alignment}`;
export type Placement = Side | AlignedPlacement;
export type Strategy = 'absolute' | 'fixed';
export type ElementRects = { reference: Rect, floating: Rect };
export type Axis = 'x' | 'y';
export type Coords = { [key in Axis]: number };
export type Length = 'width' | 'height';
export type Dimensions = { [key in Length]: number };
export type SideObject = { [key in Side]: number };
export type Rect = { x: number, y: number, width: number, height: number };
export type Padding = number | Partial<SideObject>;
export type ClientRectObject = Rect & SideObject;

const getSide = (placement: Placement): Side => placement.split('-')[0] as Side;
const getAlignment = (placement: Placement): Alignment | undefined => placement.split('-')[1] as Alignment | undefined;
const getOppositeAxis = (axis: Axis) => axis === 'x' ? 'y' : 'x';
const getAxisLength = (axis: Axis): Length => axis === 'x' ? 'width' : 'height';
const getSideAxis = (placement: Placement) => ['top', 'bottom'].includes(getSide(placement)) ? 'y' : 'x';
const getAlignmentAxis = (placement: Placement): Axis => getOppositeAxis(getSideAxis(placement));

const getElementRects = (reference: HTMLElement, floating: HTMLElement, strategy: Strategy): ElementRects => {
    //TODO: Add support for 'absolute' if we ever need it for now, if you're using computePosition, you should be using fixed positioning on your element
    //Absolute position requires more containing block calculations for things like tables, etc. See https://developer.mozilla.org/en-US/docs/Web/CSS/Containing_block
    if (strategy !== 'fixed') throw new Error('Not implemented')

    const referenceRect = reference.getBoundingClientRect();

    let offset: Coords = { x: 0, y: 0 };

    const relativeRect = {
        x: referenceRect.left - offset.x,
        y: referenceRect.top - offset.y,
        width: referenceRect.width,
        height: referenceRect.height,
    }

    return {
        reference: relativeRect,
        floating: { x: 0, y: 0, ...getDimensions(floating) }
    }
}

const getDimensions = (element: HTMLElement): Dimensions => {
    const css = getComputedStyle(element);
    let width = parseFloat(css.width) || 0;
    let height = parseFloat(css.height) || 0;
    const hasOffset = element instanceof HTMLElement;
    const offsetWidth = hasOffset ? element.offsetWidth : width;
    const offsetHeight = hasOffset ? element.offsetHeight : height;
    const shouldFallback = Math.round(width) !== offsetWidth || Math.round(height) !== offsetHeight;

    if (shouldFallback) {
        width = offsetWidth;
        height = offsetHeight;
    }

    return {
        width,
        height,
    }
}

//// Compute Position
export class ComputePositionConfig {
    placement: Placement = 'bottom'
    gap: number = 1
}

export const computePosition = (reference: HTMLElement, floating: HTMLElement, config?: Partial<ComputePositionConfig>) => {
    const cfg = {
        ...new ComputePositionConfig(),
        ...(config ?? {})
    }

    const rects = getElementRects(reference, floating, 'fixed');
    const { x, y } = computeCoordsFromPlacement(rects, cfg);

    return {
        x,
        y,
    }
}

const computeCoordsFromPlacement = ({ reference, floating }: ElementRects, config: ComputePositionConfig): Coords => {
    const sideAxis = getSideAxis(config.placement);
    const alignmentAxis = getAlignmentAxis(config.placement);
    const alignLength = getAxisLength(alignmentAxis);
    const side = getSide(config.placement);

    const commonX = (reference.x + reference.width / 2) - (floating.width / 2);
    const commonY = (reference.y + reference.height / 2) - (floating.height / 2);
    const commonAlign = (reference[alignLength] / 2) - (floating[alignLength] / 2);

    let coords: Coords;
    switch (side) {
        case 'top':
            coords = { x: commonX, y: reference.y - floating.height - config.gap };
            break;
        case 'bottom':
            coords = { x: commonX, y: reference.y + reference.height + config.gap };
            break;
        case 'right':
            coords = { x: reference.x + reference.width + config.gap, y: commonY };
            break;
        case 'left':
            coords = { x: reference.x - floating.width - config.gap, y: commonY };
            break;
        default:
            coords = { x: reference.x, y: reference.y };
    }

    switch (getAlignment(config.placement)) {
        case 'start':
            coords[alignmentAxis] -= commonAlign;
            break;
        case 'end':
            coords[alignmentAxis] += commonAlign;
            break;
        default:
    }

    return coords;
}

const isOverflowElement = (element: Element): boolean => {
    const { overflow, overflowX, overflowY, display } = getComputedStyle(element);
    return (
        /auto|scroll|overlay|hidden|clip/.test(overflow + overflowY + overflowX) &&
        !['inline', 'contents'].includes(display)
    );
}

const isLastTraversable = (node: Node): boolean => ['html', 'body', '#document'].includes(node.nodeName.toLowerCase());

const getDocument = (node: Node | Window): Document => (node instanceof Node ? node.ownerDocument : node.document) || window.document;

const getOverflowAncestors = (node: Node | null, list: OverflowAncestors = [], traverseIframes = true): OverflowAncestors => {
    if (!node) return []

    const scrollableAncestor = getNearestOverflowAncestor(node);
    const win = node.ownerDocument?.defaultView || window;

    if (scrollableAncestor === node.ownerDocument?.body) {
        return list.concat(
            win,
            win.visualViewport || [],
            isOverflowElement(scrollableAncestor) ? scrollableAncestor : [],
            traverseIframes ? getOverflowAncestors(win.frameElement) : [],
        );
    }

    return list.concat(scrollableAncestor, getOverflowAncestors(scrollableAncestor, [], traverseIframes));
}

function getNearestOverflowAncestor(node: Node): HTMLElement {
    const parent = getParent(node);

    if (isLastTraversable(parent.node)) {
        return node.ownerDocument?.body ?? (node as Document).body;
    }

    if (parent instanceof HTMLElement && isOverflowElement(parent)) {
        return parent;
    }

    return getNearestOverflowAncestor(parent.node);
}


const getParent = (node: Node) => {
    const nodeName = node instanceof Node ? node.nodeName.toLowerCase() : '#document';
    if (nodeName === 'html') {
        return { node, name: nodeName };
    }

    const result: Node =
        // Step into the shadow DOM of the parent of a slotted node.
        (node as any).assignedSlot ||
        // DOM Element detected.
        node.parentNode ||
        // ShadowRoot detected.
        (node instanceof ShadowRoot && node.host) ||
        // Fallback.
        getDocument(node).documentElement;

    return {
        node: result instanceof ShadowRoot ? result.host : result,
        name: nodeName,
    }
}


// https://samthor.au/2021/observing-dom/
const observeMove = (element: HTMLElement, onMove: () => void) => {
    let io: IntersectionObserver | null = null;
    let timeoutId: NodeJS.Timeout;

    const root = getDocument(element).documentElement;

    function cleanup() {
        clearTimeout(timeoutId);
        io?.disconnect();
        io = null;
    }

    function refresh(skip = false, threshold = 1) {
        cleanup();

        const { left, top, width, height } = element.getBoundingClientRect();

        if (!skip) {
            onMove();
        }

        if (!width || !height) {
            return;
        }

        const insetTop = Math.floor(top);
        const insetRight = Math.floor(root.clientWidth - (left + width));
        const insetBottom = Math.floor(root.clientHeight - (top + height));
        const insetLeft = Math.floor(left);
        const rootMargin = `${-insetTop}px ${-insetRight}px ${-insetBottom}px ${-insetLeft}px`;

        let isFirstUpdate = true;

        function handleObserve(entries: IntersectionObserverEntry[]) {
            const ratio = entries[0].intersectionRatio;

            if (ratio !== threshold) {
                if (!isFirstUpdate) {
                    return refresh();
                }

                if (!ratio) {
                    timeoutId = setTimeout(() => refresh(false, 1e-7), 100);
                } else {
                    refresh(false, ratio);
                }
            }

            isFirstUpdate = false;
        }

        const clippedThreshold = Math.max(0, Math.min(1, threshold) || 1);
        io = new IntersectionObserver(handleObserve, { rootMargin, threshold: clippedThreshold, root: root.ownerDocument });
        io.observe(element);
    }

    refresh(true);

    return cleanup;
}

/**
 * Listens for changes that should cause a reposition of the floating element. The update function is called whenever the position changes.
 * Ensure that the elements are actually in the DOM before calling this function. Often this requires a call to `requestAnimationFrame`.
 * @param reference The reference element to observe.
 * @param floating The floating element to reposition.
 * @param update The update function to call when the position changes.
 * @returns A cleanup function to remove the listeners.
 */
export const observePosition = (reference: HTMLElement, floating: HTMLElement, update: () => void) => {
    const ancestors = [...getOverflowAncestors(reference), ...getOverflowAncestors(floating)]

    ancestors.forEach((ancestor) => {
        ancestor.addEventListener('scroll', update, { passive: true });
        ancestor.addEventListener('resize', update);
    });

    const cleanupIntersectionObserver = observeMove(reference, update);

    let reobserveFrame = -1;
    let resizeObserver = new ResizeObserver(([firstEntry]) => {
        if (firstEntry && firstEntry.target === reference && resizeObserver) {
            // Prevent update loops when the `size` is affected by the `update` callback.
            resizeObserver.unobserve(floating);
            cancelAnimationFrame(reobserveFrame);
            reobserveFrame = requestAnimationFrame(() => {
                resizeObserver?.observe(floating);
            });
        }
        update();
    });

    resizeObserver.observe(reference);
    resizeObserver.observe(floating);

    update();

    return () => {
        ancestors.forEach((ancestor) => {
            ancestor.removeEventListener('scroll', update);
            ancestor.removeEventListener('resize', update);
        });

        cleanupIntersectionObserver();
        resizeObserver?.disconnect();
    };
}